import { createContext, ReactNode, useEffect, useState } from 'react'
import { GET, POST } from '../services/api'
import { useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { useLocalStorage } from './localstorageContext';



type LocationType = {
    id: number;
    name: string;
    code: string;
    status: boolean;
    type: string;
    cep: string;
    address: any | null;
};

type MetaType = {
    hasNextPage: boolean
    hasPreviousPage: boolean
    itemCount: number
    page: number
    pageCount: number
    take: number
}

type PluginSpaceContextData = {
    pluginSpace: any
    getPluginSpace: (fromApi?: boolean, useLocalStorage?: boolean) => any
    locations: LocationType[]
    getLocations: (fromApi?: boolean, useLocalStorage?: boolean) => Promise<LocationType[] | any[]>
    menu: MenuType[]
    getMenu: (fromApi?: boolean) => Promise<MenuType[] | any[]>
    manual: Manual[]
    integrations: IntegrationType[]
    getIntegrations: (value?: boolean, useLocalStorage?: boolean) => Promise<IntegrationType[]>
    isProtheus: boolean
    isWintor: boolean
    hasERP: boolean
    useTotvs: boolean
    usingSynkar: boolean
    usingSDS: boolean
    getPluginConfigs: (value?: boolean, useLocalStorage?: boolean) => Promise<IntegrationResponseType[]>
    metaPluginConfigs: MetaType | undefined,
    loadingGlobal: boolean
    setLoadingGlobal: (value: boolean) => void
    getGmtStore: () => Promise<string>,
    alterMenu: number,
    setAlterMenu: (value: number) => void
}

type PluginSpaceContextProvider = {
    children: ReactNode
}


type MenuType = {
    icon: string
    id: number
    name: string
    newIcon: string
    order: number
    parent: number
    route: string
    submenu: SubMenuType[]
    type: string
    permissionParent?: boolean
}

type SubMenuType = {
    id: number
    name: string
    newIcon: string
    order: number
    parent: number
    router: string
}

type Manual = {
    id: number;
    name: string;
    type: string;
    route: string;
    created?: any;
    updated?: any;
}

export type IntegrationType = {
    id: number
    name: string
    plugintemplateId: number
    content: any
    deleted: string
    plugintypeId: number

}
type IntegrationResponseType = {
    id: number;
    name: string;
    plugintemplateId: {
        id: number;
        name: string;
        parameters: string;
        plugintypeId: number;
        typeIntegration: string;
    }
}



export const PluginSpaceContext = createContext({} as PluginSpaceContextData)

const PluginSpaceProvider = ({ children }: PluginSpaceContextProvider) => {

    const [pluginSpace, setPluginSpace] = useState<any>(null)
    const [locations, setLocations] = useState<LocationType[]>([])
    const [menu, setMenu] = useState<MenuType[]>([])
    const [manual, setManual] = useState<Manual[]>([])
    const [integrations, setIntegrations] = useState<IntegrationType[]>([])
    const [pluginConfigs, setPluginConfigs] = useState<IntegrationResponseType[]>([])
    const [isWintor, setIsWintor] = useState<boolean>(false)
    const [isProtheus, setIsProtheus] = useState<boolean>(false)
    const [hasERP, setHasERP] = useState<boolean>(false)
    const [useTotvs, setUseTotvs] = useState<boolean>(false)
    const [usingSDS, setUsingSDS] = useState<boolean>(false)
    const [usingSynkar, setUsingSynkar] = useState<boolean>(false)
    const [metaPluginConfigs, setMetaPluginConfigs] = useState<MetaType | undefined>(undefined)
    const { loginInfo } = useAuth()
    const { saveItem, getItem } = useLocalStorage()

    const [alterMenu, setAlterMenu] = useState<number>(0)

    //variavel Usada para dar um loadGlobal do Admin
    const [loadingGlobal, setLoadingGlobal] = useState<boolean>(true)



    async function getPluginSpace(fromApi?: boolean, userLocalStorage?: boolean) {

        if (pluginSpace && !fromApi) {
            return pluginSpace
        }

        const contextPluginSpace = getItem('pluginSpace')

        if (contextPluginSpace && userLocalStorage) {
            setPluginSpace(JSON.parse(contextPluginSpace))
            return JSON.parse(contextPluginSpace)
        }

        const res = await GET(`/pluginspace/this`)

        if (res && res.data && res.data[0]) {
            setPluginSpace(res.data[0])

            saveItem('pluginSpace', res.data[0])

            return res.data[0]
        }

        return {}
    }

    async function getGmtStore() {
        try {
            const pluginspace = await getPluginSpace()

            if (pluginspace) {
                return pluginspace.gmt
            } else {
                return '+00:00'
            }
        } catch (e) {
            return '+00:00'
        }
    }

    async function getPluginConfigs(update?: boolean, useLocalStorage?: boolean) {
        try {

            if (pluginConfigs && pluginConfigs.length && !update) {
                return pluginConfigs
            }


            const contextPluginConfig = getItem('pluginConfig')

            if (contextPluginConfig && useLocalStorage) {
                setPluginConfigs(JSON.parse(contextPluginConfig))

                return JSON.parse(contextPluginConfig)
            }

            const res = await POST(`/pluginconfig/filter`, {})

            if (res && res.data && res.data.length) {
                setPluginConfigs(res.data)
                setMetaPluginConfigs(res.meta)

                saveItem('pluginConfig', res.data)

                return res.data
            }

            return []
        } catch (error) {
            console.log(error)
            return []
        }
    }


    async function getLocations(fromApi?: boolean, useLocalStorage?: boolean) {
        if (locations && locations.length > 0 && !fromApi) {
            return locations
        }

        const contextLocations = getItem('locations')

        if (contextLocations && useLocalStorage) {
            setLocations(
                JSON.parse(contextLocations)
            );
            return JSON.parse(contextLocations)

        }

        const response = await GET(`/location`)

        const newLocations = response.data.map(
            (location: LocationType) => ({
                id: location.id,
                name: location.name,
                code: location.code,
                status: location.status,
                type: location.type,
                cep: location.cep,
                address: location.address
            })
        );

        setLocations(
            newLocations
        );

        saveItem('locations', newLocations)
        return newLocations

    }

    async function getMenu(fromApi?: boolean) {
        try {
            if (menu.length) {
                return menu
            }


            const response = await GET('/pages/buildMenuPage')
            if (response) {
                const menuOrder = response.sort((a: MenuType, b: MenuType) => a.order - b.order)
                setMenu(menuOrder)

                return menuOrder
            }
        } catch (error) {
            console.log(error)
        }
    }


    async function getManual(fromApi?: boolean) {
        if (manual.length && !fromApi) {
            return manual
        }
        const contextManual = getItem('manual')
        if (contextManual) {
            setManual(JSON.parse(contextManual))
            return JSON.parse(contextManual)
        }

        const response = await GET('manual')

        if (response && response.data) {
            setManual(response.data)
            return response.data
        }

    }

    async function getIntegrations(update?: boolean, useLocalStorage?: boolean) {

        try {

            let data: IntegrationType[] = []


            const contextIntegrations = getItem('integrations')

            if (integrations && integrations.length && !update) {
                data = integrations
            } else if (contextIntegrations && useLocalStorage) {
                data = JSON.parse(contextIntegrations)
            } else {

                const integrationRes = await GET(`/pluginconfig`)

                data = integrationRes.data

                saveItem('integrations', data)
            }
            setIntegrations(data)

            const findWintorIntegration = data.find(el => el.plugintemplateId === 45)

            if (findWintorIntegration) {
                setIsWintor(true)
            }

            const findProtheusIntegration = data.find(el => el.plugintemplateId === 104)

            if (findProtheusIntegration) {
                setIsProtheus(true)
            }

            const totvsIntegration = data.filter(
                (pluginconfig: any) =>
                    pluginconfig.plugintemplateId === 23 ||
                    pluginconfig.plugintemplateId === 15 ||
                    pluginconfig.plugintemplateId === 45 ||
                    // pluginconfig.plugintemplateId === 48 ||
                    pluginconfig.plugintemplateId === 104
            )

            if (totvsIntegration && totvsIntegration.length > 0)
                setHasERP(true)
            else
                setHasERP(false)

            const totvsHas = data.find(
                (integration: IntegrationType) =>
                    integration.plugintemplateId === 23 ||
                    integration.plugintemplateId === 15 ||
                    integration.plugintemplateId === 45
            )
            if (
                (totvsHas && totvsHas.content && totvsHas.content.able === '1') ||
                (totvsHas && totvsHas.plugintemplateId === 45)
            ) {
                setUseTotvs(true)
            } else {
                setUseTotvs(false)
            }

            const sdsIntegration = data.find(
                (integration: IntegrationType) =>
                    integration.plugintemplateId === 101
            )
            if (
                (sdsIntegration && sdsIntegration.content && sdsIntegration.content.able === '1') ||
                (sdsIntegration && sdsIntegration.plugintemplateId === 45)
            ) {

                setUsingSDS(true)
            }

            const synCarIntegration = data.find(
                (integration: IntegrationType) =>
                    integration.plugintemplateId === 108
            )
            if (synCarIntegration && synCarIntegration.content && synCarIntegration.content.able === '1') {
                setUsingSynkar(true)
            }

            return data

        } catch (e) {
            console.log(e)
            return []
        }

    }

    async function loadAll() {
        try {
            setLoadingGlobal(true)
            await Promise.all([
                getPluginSpace(true),
                getMenu(true),
                getLocations(true),
                getManual(true),
                getIntegrations(true, true),
                getPluginConfigs(true, true),
            ])

            setLoadingGlobal(false)
        } catch (error) {
            console.log(error)
            setLoadingGlobal(false)
        }
    }


    useEffect(() => {
        loadAll()
    }, [loginInfo.pluginspaceId])


    return (
        <PluginSpaceContext.Provider
            value={{
                pluginSpace,
                getPluginSpace,
                getLocations,
                locations,
                menu,
                getMenu,
                manual,
                isProtheus,
                isWintor,
                integrations,
                hasERP,
                useTotvs,
                usingSDS,
                usingSynkar,
                getIntegrations,
                getPluginConfigs,
                metaPluginConfigs,
                loadingGlobal,
                setLoadingGlobal,
                getGmtStore,
                alterMenu,
                setAlterMenu
            }}
        >
            {children}
        </PluginSpaceContext.Provider>
    )
}

export default PluginSpaceProvider